import { useEffect, useState } from 'react'

import { Alert } from 'components/Alert'
import { AppLayout } from 'components/AppLayout'
import { Button } from 'components/Button'
import { pageCardClasses } from 'components/AppLayout/styles'
import { commonStrings, oaCreationStrings } from 'constants/strings'
import {
  EOaCreationJobStatus,
  type TOaCreationJob,
} from 'types'
import { useOaCreationJob, useOaCreationJobs } from 'hooks'
import { CreateForm } from './CreateForm'
import { JobsHistoryTable } from './JobsHistoryTable'
import { JobsHistoryCardList } from './JobsHistoryCardList'

const ACTIVE_STATUSES: EOaCreationJobStatus[] = [
  EOaCreationJobStatus.PENDING,
  EOaCreationJobStatus.RUNNING,
]

const PAGE_SIZE = 10

export default function OaCreation() {
  const [trackedJobId, setTrackedJobId] = useState<number | undefined>()
  const [page, setPage] = useState(1)

  const {
    data: listData,
    loading: isListLoading,
    error: listError,
    refetch: refetchList,
  } = useOaCreationJobs({
    currentPage: page,
    pageSize: PAGE_SIZE,
  })

  const { job: activeJob, refetch: refetchActiveJob } = useOaCreationJob({
    jobId: trackedJobId,
  })

  const [isReloading, setIsReloading] = useState(false)

  const total = listData?.total ?? 0
  const currentPage = listData?.currentPage ?? page
  const totalPage = listData?.totalPage ?? 0

  // ページ範囲外になった場合に補正する（例: 削除で総ページ数が減ったとき）
  useEffect(() => {
    if (isListLoading) return

    if (totalPage === 0 && page !== 1) {
      setPage(1)
      return
    }

    if (totalPage > 0 && page > totalPage) {
      setPage(totalPage)
    }
  }, [isListLoading, page, totalPage])

  // 追跡対象のジョブが終端ステータスに到達したら追跡を解除する
  useEffect(() => {
    if (!activeJob) return
    if (!ACTIVE_STATUSES.includes(activeJob.status)) {
      setTrackedJobId(undefined)
      refetchList()
    }
  }, [activeJob, refetchList])

  // 一覧再ロード時に進行中ジョブが見つかれば自動追跡開始（画面遷移後の復帰用）
  useEffect(() => {
    if (trackedJobId) return
    const inflight = listData?.data.find((job) =>
      ACTIVE_STATUSES.includes(job.status),
    )
    if (inflight) {
      setTrackedJobId(inflight.id)
    }
  }, [listData, trackedJobId])

  const handleCreated = (job: TOaCreationJob) => {
    setTrackedJobId(job.id)
    setPage(1)
  }

  const pageLabel =
    totalPage > 0
      ? `${currentPage} / ${totalPage}`
      : oaCreationStrings.pagination.empty
  const canMovePrev = !isListLoading && totalPage > 0 && currentPage > 1
  const canMoveNext = !isListLoading && totalPage > 0 && currentPage < totalPage

  const handlePrev = () => {
    if (!canMovePrev) return
    setPage((prev) => Math.max(1, prev - 1))
  }

  const handleNext = () => {
    if (!canMoveNext) return
    setPage((prev) => prev + 1)
  }

  const handleReload = async () => {
    setIsReloading(true)
    try {
      await Promise.all([refetchList(), refetchActiveJob()])
    } finally {
      setIsReloading(false)
    }
  }

  return (
    <AppLayout>
      <section className={`w-full ${pageCardClasses.regular}`}>
        <header className="grid gap-2">
          <h1 className="text-2xl font-display text-ink">
            {oaCreationStrings.title}
          </h1>
          <p className="text-sm text-muted">{oaCreationStrings.description}</p>
        </header>

        <div className="mt-6">
          <CreateForm onCreated={handleCreated} />
        </div>

        {activeJob && (
          <div className="mt-6 rounded-2xl border border-accent/40 bg-accent-soft/40 p-4">
            <div className="flex flex-wrap items-center gap-2 text-xs text-ink">
              <span className="font-semibold">{activeJob.accountName}</span>
              <span className="text-muted">#{activeJob.id}</span>
              <span className="rounded-full bg-white px-2 py-0.5 font-semibold text-accent-strong">
                {oaCreationStrings.status[activeJob.status]}
              </span>
              <span className="text-muted">
                {oaCreationStrings.table.progressWithFailedTemplate
                  .replace('$success', String(activeJob.successCount))
                  .replace('$failed', String(activeJob.failedCount))
                  .replace('$total', String(activeJob.quantity))}
              </span>
            </div>
          </div>
        )}

        <div className="mt-8 grid gap-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-base font-semibold text-ink">
              {oaCreationStrings.table.title}
            </h2>
            <div className="flex w-full flex-wrap items-center justify-between gap-3 sm:w-auto sm:justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void handleReload()}
                disabled={isListLoading || isReloading}
              >
                {isReloading
                  ? oaCreationStrings.actions.reloading
                  : oaCreationStrings.actions.reload}
              </Button>
              <span className="rounded-full border border-border/70 bg-white px-3 py-1 text-xs font-semibold text-muted">
                {`${total}${commonStrings.countSuffix}`}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handlePrev}
                  disabled={!canMovePrev}
                >
                  {oaCreationStrings.pagination.previous}
                </Button>
                <span className="min-w-[86px] text-center text-xs font-semibold text-muted">
                  {pageLabel} {oaCreationStrings.pagination.page}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleNext}
                  disabled={!canMoveNext}
                >
                  {oaCreationStrings.pagination.next}
                </Button>
              </div>
            </div>
          </div>

          {listError && <Alert>{listError}</Alert>}

          <div className="hidden overflow-x-auto rounded-2xl border border-border/70 bg-white table-scroll lg:block">
            <JobsHistoryTable
              jobs={listData?.data ?? []}
              loading={isListLoading && !listData}
            />
          </div>
          <div className="lg:hidden">
            <JobsHistoryCardList
              jobs={listData?.data ?? []}
              loading={isListLoading && !listData}
              className="md:grid-cols-2"
            />
          </div>
        </div>
      </section>
    </AppLayout>
  )
}
